import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError, uploadImages } from "@/lib/api";
import { useMyCardBacks } from "@/lib/queries";

export function DeckBackPicker({ value, onChange, deckName }) {
  const qc = useQueryClient();
  const { data: mine = [] } = useMyCardBacks();
  const { data: library } = useQuery({ queryKey: ["card-backs", "picker"], queryFn: () => api.get("/card-backs", { params: { page_size: 100 } }).then((r) => r.data.items) });
  const [busy, setBusy] = useState(false);
  const options = [...mine, ...(library || []).filter((b) => !mine.some((m) => m.card_back_id === b.card_back_id))];
  const current = options.find((b) => b.card_back_id === value);

  const upload = async (file) => {
    setBusy(true);
    try {
      const [res] = await uploadImages([file], "card_back");
      if (!res.ok) throw new Error(res.error);
      const { data } = await api.post("/card-backs", { name: `${deckName} back`.slice(0, 100), image_file_id: res.file_id, visibility: "public" });
      qc.invalidateQueries({ queryKey: ["me", "card-backs"] });
      onChange(data.card_back_id);
      toast.success("Card back uploaded and selected");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel space-y-3 p-4" data-testid="deck-back-picker">
      <p className="eyebrow text-[10px]">Deck card back</p>
      <div className="flex items-center gap-3">
        {current ? <img src={assetUrl(current.image_url)} alt={current.name} className="h-16 w-12 rounded object-cover" data-testid="deck-back-preview" />
          : <div className="flex h-16 w-12 items-center justify-center rounded border border-dashed border-[#2A3447] text-[9px] text-slate-500">Default</div>}
        <select className="field text-xs" value={value || ""} onChange={(e) => onChange(e.target.value || null)} aria-label="Choose card back" data-testid="deck-back-select">
          <option value="">Default back</option>
          {options.map((b) => <option key={b.card_back_id} value={b.card_back_id}>{b.name}{b.owner ? ` — @${b.owner.username}` : ""}</option>)}
        </select>
      </div>
      <label className="btn-ghost w-full cursor-pointer text-xs" data-testid="deck-back-upload-label">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload a new back
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="deck-back-upload-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; f && upload(f); }} />
      </label>
    </div>
  );
}
